import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { FETCH_SHIPMENT, SPLIT_SHIPMENT } from "../graphql";
import SourceProducts from "./SourceProducts";
import { SplitProvider, useSplit } from "./SplitContext";
import TargetShipments from "./TargetShipments";

function SplitPage({ loading, onSubmit }) {
  const { shipment, targets, canSubmit } = useSplit();

  return (
    <Page
      backTo={`/shipment/shipments/${shipment.id}`}
      title={`Split Shipment #${shipment.id}`}
      subtitle="Assign every carton once. New shipments will keep the shipment logistics data."
      rightButtons={
        <Button
          primary
          loading={loading}
          disabled={!canSubmit}
          onClick={() => {
            if (canSubmit) onSubmit(targets);
          }}
        >
          Create {targets.length} Shipments
        </Button>
      }
    >
      <div className="grid flex-1 gap-6 overflow-auto p-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)] lg:p-6">
        <SourceProducts />
        <TargetShipments />
      </div>
    </Page>
  );
}

export default function SplitShipment() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { loading, error, data } = useQuery(FETCH_SHIPMENT, {
    variables: { id },
    fetchPolicy: "network-only",
  });

  const [splitShipment, splitShipmentResult] = useMutation(SPLIT_SHIPMENT);

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const { shipment } = data;
  const canSplit =
    shipment.isEffective &&
    !shipment.source &&
    shipment.children.edges.length === 0;

  async function submit(targets) {
    try {
      const { data: result } = await splitShipment({
        variables: {
          shipmentId: shipment.id,
          targets: targets.map((target) => ({
            allocations: target.allocations,
          })),
        },
        refetchQueries: [
          { query: FETCH_SHIPMENT, variables: { id: shipment.id } },
        ],
        awaitRefetchQueries: true,
      });
      const { splitShipment: splitResult } = result;
      Alert("success", `${splitResult.shipments.length} shipments created.`);
      navigate(`/shipment/shipments/${splitResult.sourceShipment.id}`);
    } catch (mutationError) {
      Alert("error", parseError(mutationError));
    }
  }

  if (!canSplit) {
    return (
      <Page
        backTo={`/shipment/shipments/${id}`}
        title={`Split Shipment #${id}`}
      >
        <div className="m-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
          Only an effective root shipment that has not been split can be split.
        </div>
      </Page>
    );
  }

  // Keep data fetching at the page boundary; split state belongs to its panels.
  return (
    <SplitProvider shipment={shipment}>
      <SplitPage loading={splitShipmentResult.loading} onSubmit={submit} />
    </SplitProvider>
  );
}
